/**
 * ValidationManagementConsole — Package 3 UI.
 *
 * Provider-agnostic validation management. Uses only /api/engage/v2/validation/*
 * canonical routes. No redesign; reuses shadcn primitives.
 *
 * Credential fields are write-only via an opaque `credentialsRef` handle —
 * secrets are NEVER surfaced by the API and this UI never displays or
 * transmits them. Statements-only preserved; no payment execution.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/engage/v2/validation`;
const VALIDATION_TYPES = ["postcode", "address", "telephone", "email",
                            "bank_account", "age", "campaign_eligibility"];
const REVIEW_DECISIONS = ["accept", "reject", "request_correction", "retry"];
const WRITE_ROLES = new Set([
  "platform_owner", "platform_administrator",
  "owner", "director", "admin", "manager", "regional_manager",
]);

const auth = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("taptrack_token") || ""}` },
});
const humanise = (e) => e?.response?.data?.detail?.message
  || e?.response?.data?.message || e?.response?.data?.detail?.code
  || e?.message || "Something went wrong.";

function StatusBadge({ value }) {
  const map = {
    valid: "default", invalid: "destructive",
    unavailable: "secondary", provider_error: "destructive",
    indeterminate: "outline", unsupported: "outline", not_attempted: "outline",
  };
  return <Badge variant={map[value] || "outline"} data-testid={`status-${value}`}>{value}</Badge>;
}

function OverviewTab() {
  const [caps, setCaps] = useState([]);
  const [err, setErr] = useState(null);
  useEffect(() => {
    axios.get(`${API}/capabilities`, auth())
      .then((r) => setCaps(r.data.capabilities || []))
      .catch((e) => setErr(humanise(e)));
  }, []);
  return (
    <Card data-testid="validation-overview">
      <CardHeader><CardTitle>Validation Overview</CardTitle></CardHeader>
      <CardContent>
        {err && <div className="text-red-600 text-sm" data-testid="validation-overview-error">{err}</div>}
        <Table>
          <TableHeader><TableRow>
            <TableHead>Validation type</TableHead><TableHead>Resolved provider</TableHead>
            <TableHead>External?</TableHead><TableHead>Selection chain</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {caps.map((c) => (
              <TableRow key={c.validationType} data-testid={`cap-row-${c.validationType}`}>
                <TableCell>{c.validationType}</TableCell>
                <TableCell className="font-mono text-xs">{c.resolvedProviderId}</TableCell>
                <TableCell>{c.isExternal ? "yes" : <Badge variant="outline">local</Badge>}</TableCell>
                <TableCell className="font-mono text-xs">{(c.selectionChain || []).join(" → ")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="text-xs text-muted-foreground mt-2">
          Local adapters are labelled truthfully — <code>local_format_validation</code> or
          <code> deterministic_rule_validation</code>. They are <strong>not</strong> external verification.
        </div>
      </CardContent>
    </Card>
  );
}

function ProviderConfigTab({ user, canWrite }) {
  const isPlatform = ["platform_owner", "platform_administrator"]
    .includes((user?.role || "").toLowerCase());
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    scope: isPlatform ? "platform" : "organisation",
    validationType: "email", providerId: "", status: "active",
    credentialsRef: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const refresh = () => axios.get(`${API}/providers`, auth())
    .then((r) => setRows(r.data.providers || []))
    .catch(() => setRows([]));
  useEffect(() => { refresh(); }, []);
  const submit = async () => {
    setErr(null); setSubmitting(true);
    try {
      await axios.post(`${API}/providers`, form, auth());
      toast.success("Provider configuration saved.");
      setOpen(false); refresh();
    } catch (e) { setErr(humanise(e)); } finally { setSubmitting(false); }
  };
  return (
    <Card data-testid="validation-providers">
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Provider Status</CardTitle>
        {canWrite && (
          <Button size="sm" onClick={() => setOpen(true)}
                  data-testid="providers-configure-btn">Configure provider</Button>
        )}
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Scope</TableHead><TableHead>Org</TableHead>
            <TableHead>Validation type</TableHead><TableHead>Provider ID</TableHead>
            <TableHead>Status</TableHead><TableHead>Updated</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6"
                                     data-testid="providers-empty">
                No providers configured — all validations use local adapters.
              </TableCell></TableRow>
            )}
            {rows.map((p) => (
              <TableRow key={p.id} data-testid={`provider-row-${p.id}`}>
                <TableCell><Badge variant="outline">{p.scope}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{p.organisationId || "—"}</TableCell>
                <TableCell>{p.validationType}</TableCell>
                <TableCell className="font-mono text-xs">{p.providerId}</TableCell>
                <TableCell>{p.status}</TableCell>
                <TableCell className="text-xs">{p.updatedAt}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="text-xs text-muted-foreground mt-2">
          Credential values are never shown or returned by the API — only an opaque
          <code> credentialsRef</code> is stored. Where secure storage is not available,
          leave the reference empty and the platform will fall back to local adapters.
        </div>
      </CardContent>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="provider-config-dialog">
          <DialogHeader>
            <DialogTitle>Configure Provider</DialogTitle>
            <DialogDescription>Secrets are NEVER entered here.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Scope</Label>
              <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                <SelectTrigger data-testid="provider-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {isPlatform && <SelectItem value="platform">platform</SelectItem>}
                  <SelectItem value="organisation">organisation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Validation type</Label>
              <Select value={form.validationType}
                      onValueChange={(v) => setForm({ ...form, validationType: v })}>
                <SelectTrigger data-testid="provider-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VALIDATION_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Provider ID (external adapter id)</Label>
              <Input value={form.providerId}
                     onChange={(e) => setForm({ ...form, providerId: e.target.value })}
                     data-testid="provider-id" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="provider-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">active</SelectItem>
                  <SelectItem value="inactive">inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>credentialsRef (opaque, never a secret)</Label>
              <Input value={form.credentialsRef}
                     onChange={(e) => setForm({ ...form, credentialsRef: e.target.value })}
                     placeholder="vault://…"
                     data-testid="provider-credentials-ref" />
            </div>
          </div>
          {err && <div className="text-red-700 text-sm mt-2" data-testid="provider-config-error">{err}</div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}
                    data-testid="provider-config-cancel">Cancel</Button>
            <Button onClick={submit} disabled={submitting}
                    data-testid="provider-config-submit">
              {submitting ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function HistoryTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("__all__");
  const [statusFilter, setStatusFilter] = useState("__all__");
  const refresh = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (typeFilter !== "__all__") qs.set("validationType", typeFilter);
    if (statusFilter !== "__all__") qs.set("status", statusFilter);
    try {
      const r = await axios.get(`${API}/results?${qs.toString()}`, auth());
      setRows(r.data.results || []);
    } catch { setRows([]); }
    setLoading(false);
  }, [typeFilter, statusFilter]);
  useEffect(() => { refresh(); }, [refresh]);
  return (
    <Card data-testid="validation-history">
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Validation History</CardTitle>
        <Button variant="outline" size="sm" onClick={refresh}
                data-testid="history-refresh">Refresh</Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="history-filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                {VALIDATION_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="history-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                {["valid", "invalid", "unavailable", "unsupported",
                  "indeterminate", "provider_error", "not_attempted"].map(
                    (s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Table data-testid="history-table">
          <TableHeader><TableRow>
            <TableHead>ID</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead>
            <TableHead>Provider</TableHead><TableHead>Masked input</TableHead>
            <TableHead>Completed</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6}
              className="text-center py-4 text-muted-foreground">Loading…</TableCell></TableRow>}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={6}
                className="text-center py-6 text-muted-foreground"
                data-testid="history-empty">No validation history in scope.</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`history-row-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.id}</TableCell>
                <TableCell>{r.validationType}</TableCell>
                <TableCell><StatusBadge value={r.normalisedStatus} /></TableCell>
                <TableCell className="font-mono text-xs">{r.providerId}</TableCell>
                <TableCell className="font-mono text-xs">{r.maskedInput || "—"}</TableCell>
                <TableCell className="text-xs">{r.completedAt}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function ReviewQueueTab({ canReview }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    validationIds: "", reviewDecision: "accept", reason: "", note: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const refresh = () => axios.get(`${API}/reviews`, auth())
    .then((r) => setRows(r.data.reviews || []))
    .catch(() => setRows([]));
  useEffect(() => { if (canReview) refresh(); }, [canReview]);
  const submit = async () => {
    setErr(null); setSubmitting(true);
    try {
      const body = { ...form,
        validationIds: form.validationIds.split(",").map((s) => s.trim()).filter(Boolean) };
      await axios.post(`${API}/reviews`, body, auth());
      toast.success("Manual review recorded (immutable).");
      setOpen(false); refresh();
    } catch (e) { setErr(humanise(e)); } finally { setSubmitting(false); }
  };
  if (!canReview) return (
    <Card><CardContent className="py-8 text-center text-muted-foreground"
                        data-testid="review-forbidden">
      You do not have permission to view or manage the review queue.
    </CardContent></Card>
  );
  return (
    <Card data-testid="validation-reviews">
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Review Queue</CardTitle>
        <Button size="sm" onClick={() => setOpen(true)}
                data-testid="reviews-create-btn">Record review</Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow>
            <TableHead>ID</TableHead><TableHead>Decision</TableHead>
            <TableHead>Reviewer</TableHead><TableHead>Validation refs</TableHead>
            <TableHead>Created</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={5}
                className="text-center py-6 text-muted-foreground"
                data-testid="reviews-empty">No manual reviews recorded yet.</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`review-row-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.id}</TableCell>
                <TableCell><Badge variant="outline">{r.reviewDecision}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{r.reviewerId}</TableCell>
                <TableCell className="font-mono text-xs">{(r.validationIds || []).join(", ")}</TableCell>
                <TableCell className="text-xs">{r.createdAt}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle>Record Manual Review</DialogTitle>
            <DialogDescription>
              This creates a NEW immutable review event. It does not modify any
              existing validation record.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Validation IDs (comma-separated)</Label>
              <Input value={form.validationIds}
                     onChange={(e) => setForm({ ...form, validationIds: e.target.value })}
                     data-testid="review-ids" />
            </div>
            <div>
              <Label>Decision</Label>
              <Select value={form.reviewDecision}
                      onValueChange={(v) => setForm({ ...form, reviewDecision: v })}>
                <SelectTrigger data-testid="review-decision"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REVIEW_DECISIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason</Label>
              <Input value={form.reason}
                     onChange={(e) => setForm({ ...form, reason: e.target.value })}
                     data-testid="review-reason" />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea rows={2} value={form.note}
                        onChange={(e) => setForm({ ...form, note: e.target.value })}
                        data-testid="review-note" />
            </div>
          </div>
          {err && <div className="text-red-700 text-sm mt-2" data-testid="review-error">{err}</div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}
                    data-testid="review-cancel">Cancel</Button>
            <Button onClick={submit} disabled={submitting}
                    data-testid="review-submit">
              {submitting ? "Recording…" : "Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function DetailTab() {
  const [id, setId] = useState("");
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState(null);
  const load = async () => {
    setErr(null); setDetail(null);
    try {
      const r = await axios.get(`${API}/results/${id}`, auth());
      setDetail(r.data.result);
    } catch (e) { setErr(humanise(e)); }
  };
  return (
    <Card data-testid="validation-detail">
      <CardHeader><CardTitle>Validation Detail</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Label>Validation ID</Label>
            <Input value={id} onChange={(e) => setId(e.target.value)}
                   placeholder="val-…" data-testid="detail-id" />
          </div>
          <Button onClick={load} disabled={!id} data-testid="detail-load">Load</Button>
        </div>
        {err && <div className="text-red-700 text-sm" data-testid="detail-error">{err}</div>}
        {detail && (
          <div className="grid grid-cols-2 gap-3 text-sm" data-testid="detail-body">
            <div><strong>Type:</strong> {detail.validationType}</div>
            <div><strong>Status:</strong> <StatusBadge value={detail.normalisedStatus} /></div>
            <div><strong>Provider:</strong> {detail.providerId}</div>
            <div><strong>Classification:</strong> {detail.providerResponseClassification}</div>
            <div><strong>Latency:</strong> {detail.latencyMs} ms</div>
            <div><strong>Retryable:</strong> {String(!!detail.retryable)}</div>
            <div className="col-span-2"><strong>Reason:</strong> {detail.reasonCode || "—"}</div>
            <div className="col-span-2"><strong>Explanation:</strong> {detail.explanation || "—"}</div>
            <div className="col-span-2"><strong>Fallback chain:</strong>
              <span className="font-mono text-xs ml-2">{(detail.fallbackChain || []).join(" → ")}</span>
            </div>
            <div className="col-span-2"><strong>Masked input:</strong>
              <span className="font-mono text-xs ml-2">{detail.maskedInput || "—"}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PolicyTab() {
  return (
    <Card data-testid="validation-policy">
      <CardHeader><CardTitle>Campaign Validation Policy</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div>Campaign-level validation policy is governed by Package 2 —
          it lives on the campaign&apos;s <code>validationRules</code> object.</div>
        <div>Where no governed policy exists, the platform applies the safe
          default and records <code>policySource=default</code> on the outcome.</div>
        <div>The default policy requires: postcode, telephone, email, age and
          campaign eligibility; optional: address, bank account. Any
          <code> invalid</code> on a required field rejects the submission.
          <code> unavailable</code> or <code> provider_error</code> on a required
          field routes the submission to manual review.</div>
      </CardContent>
    </Card>
  );
}

export default function ValidationManagementConsole() {
  const { user: ctxUser } = useAuth();
  const user = ctxUser || null;
  const role = (user?.role || "").toLowerCase();
  const canWrite = WRITE_ROLES.has(role);
  const canReview = canWrite ||
    ["platform_auditor", "platform_analyst", "platform_support"].includes(role);
  useEffect(() => { document.title = "Engage — Validation Management"; }, []);
  if (!user) return (
    <div className="p-6 text-center text-muted-foreground" data-testid="validation-noauth">
      Sign in required.
    </div>
  );
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="engage-v2-validation-console">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-3xl font-semibold">Validation Management</h1>
          <p className="text-sm text-muted-foreground">
            Provider-agnostic operational validation. Statements-only — no payment execution.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">Engage v2</Badge>
          <Badge variant={canWrite ? "default" : "secondary"} data-testid="validation-role-badge">
            {canWrite ? "write" : (canReview ? "review" : "read-only")}
          </Badge>
        </div>
      </div>
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid grid-cols-6 w-full" data-testid="validation-tabs">
          <TabsTrigger value="overview" data-testid="validation-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="providers" data-testid="validation-tab-providers">Provider Status</TabsTrigger>
          <TabsTrigger value="policy" data-testid="validation-tab-policy">Campaign Policy</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="validation-tab-reviews">Review Queue</TabsTrigger>
          <TabsTrigger value="history" data-testid="validation-tab-history">History</TabsTrigger>
          <TabsTrigger value="detail" data-testid="validation-tab-detail">Detail</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><OverviewTab /></TabsContent>
        <TabsContent value="providers"><ProviderConfigTab user={user} canWrite={canWrite} /></TabsContent>
        <TabsContent value="policy"><PolicyTab /></TabsContent>
        <TabsContent value="reviews"><ReviewQueueTab canReview={canReview} /></TabsContent>
        <TabsContent value="history"><HistoryTab /></TabsContent>
        <TabsContent value="detail"><DetailTab /></TabsContent>
      </Tabs>
    </div>
  );
}
